import { useQuery } from '@tanstack/react-query';
import { Divider, Form, FormProps } from 'antd';
import { SelectProps } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useSearchParams } from 'react-router';

import useFormDraftInit from '@/hooks/useFormDraftInit';
import useFormFieldsDependences from '@/hooks/useFormFieldsDependences';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import CompanyContact from '@/model/General/CompanyContact';
import { Currency } from '@/model/General/Currency';
import { Technician } from '@/model/General/Technician';
import { SERVICE_ORDER_STATUS, SERVICE_ORDER_TYPE } from '@/model/ServiceOrder/ServiceOrder';
import { Ticket } from '@/model/Ticket/Ticket';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

const useServiceOrderForm = (formProps: FormProps) => {
  const { t } = useTranslation();
  const { companyRepository } = useRepositoriesContext();
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') ? Number(searchParams.get('company')) : undefined;
  const ticket = searchParams.get('ticket') ? Number(searchParams.get('ticket')) : undefined;
  const [selectedTechnician, setSelectedTechnician] = useState<Technician>(
    formProps.initialValues?.technician ?? null,
  );
  const flattenInitialValues = useMemo(
    () =>
      formProps.initialValues
        ? {
            ...formProps.initialValues,
            tickets: formProps.initialValues?.tickets.map((ticket: Ticket) => ticket.id) ?? [],
            contact_data: formProps.initialValues?.contact_data
              ? parseInt(formProps.initialValues?.contact_data)
              : undefined,
            currency: formProps.initialValues?.currency?.id,
            hours_covered: formProps.initialValues?.hours_covered ?? 0,
            kilometres_covered: formProps.initialValues?.kilometres_covered ?? 0,
            moviles: formProps.initialValues?.moviles.map((m: any) => m.id) ?? [],
          }
        : {
            tickets: ticket ? [ticket] : [],
            status: SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION,
            company: company,
            hours_covered: 0,
            kilometres_covered: 0,
          },
    [company, formProps.initialValues, ticket],
  );
  const { formInitialValues, initialValuesSetted, clearDraft } = useFormDraftInit({
    form: formProps.form,
    draftEnabled: true,
    initialValues: flattenInitialValues,
  });

  useFormFieldsDependences(formProps.form, formInitialValues ?? {}, initialValuesSetted, [
    {
      watchField: 'company',
      resetFields: ['tickets', 'movil'],
    },
  ]);
  const selectedCompany = Form.useWatch('company', formProps.form);
  // selectores
  const { companySelectProps, technicianSelectProps } = useOutletContext<{
    companySelectProps: SelectProps;
    technicianSelectProps: SelectProps;
  }>();
  const { selectProps: ticketsSelectProps } = useSelectScrollSearch(
    {
      resource: RESOURCES.TICKETS,
      optionLabel: (item: any) =>
        `${item.id} - ${item.title ? item.title : item.ticket_title_str ? item.ticket_title_str : '-'}`,
      optionValue: (item: any) => item.id,
      queryOptions: {
        enabled: !!selectedCompany,
      },
      filters: [
        {
          value: selectedCompany,
          operator: 'eq' as const,
          field: 'empresa',
        },
      ],
    },
    {
      saveItem: true,
    },
  );
  const { selectProps: currencySelectProps } = useSelectScrollSearch(
    {
      resource: 'currencies',
      optionLabel: (item: Currency) => item.nombre + ' (' + item.abreviacion + ')',
      pagination: {
        mode: 'off',
        pageSize: 3000,
      },
    },
    { saveItem: true },
  );
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps,
    resource: 'empresas',
    initialValue:
      formInitialValues?.company != formInitialValues?.shipment_payment_company
        ? [formInitialValues?.company, formInitialValues?.shipment_payment_company]
        : formInitialValues?.company,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const technicianSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: technicianSelectProps,
    resource: 'technicians',
    initialValue: formInitialValues?.technician,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: formInitialValues?.tickets,
    labelKey: 'title',
    valueKey: 'id',
  });
  //#region Contacto de la empresa
  const { data: companyContactData, isFetching: loadingCompanyContactData } = useQuery<
    CompanyContact[]
  >({
    queryKey: ['companyContacts', selectedCompany],
    queryFn: () => companyRepository.getCompanyContact(selectedCompany as number),
    enabled: selectedCompany !== undefined,
  });
  const companyContact = Form.useWatch('company_contact', formProps.form);
  const [companyContactWarning, setCompanyContactWarning] = useState<string>();

  // Seteo de warning si no tiene contactos

  useEffect(() => {
    if (selectedCompany === undefined || loadingCompanyContactData) {
      return;
    }

    if (selectedCompany && companyContactData?.length === 0) {
      formProps.form?.setFields([
        {
          name: 'company',
          warnings: [t('La empresa o técnico no tiene un contacto asignado')],
        },
      ]);
    } else {
      formProps.form?.setFields([
        {
          name: 'company',
          warnings: [],
        },
      ]);
    }
  }, [selectedCompany, formProps.form, loadingCompanyContactData, t, companyContactData?.length]);

  //Warning si el contacto no tiene email
  useEffect(() => {
    if (!companyContactData || !companyContact) {
      setCompanyContactWarning(undefined);

      return;
    }

    const contactId = typeof companyContact === 'string' ? Number(companyContact) : companyContact;
    const selectedContact = [...(companyContactData ?? [])].find(
      contact => contact.id === contactId,
    );

    if (selectedContact && !selectedContact.email) {
      setCompanyContactWarning(t('El contacto seleccionado no tiene un email asignado'));
    } else {
      setCompanyContactWarning(undefined);
    }
  }, [companyContact, companyContactData, t]);

  // Armado de opciones de contacto
  const formatContactOption = (contact: CompanyContact) => ({
    label: (
      <>
        {contact.name}
        {contact.email ? (
          <>
            <Divider type="vertical" /> {contact.email}
          </>
        ) : null}
      </>
    ),
    value: contact.id,
  });
  const contactOptions = useMemo(() => {
    if (companyContactData?.length) {
      return companyContactData.map(formatContactOption);
    }
  }, [companyContactData]);
  const companyContactRules = useMemo(() => {
    return [
      {
        required: true,
        message: t('Debe seleccionar un contacto'),
      },
      {
        validator: () => {
          if (companyContactWarning) {
            return Promise.reject(new Error(companyContactWarning));
          }

          return Promise.resolve();
        },
      },
    ];
  }, [companyContactWarning, t]);
  const companyContactsSelectProps: SelectProps = {
    options: contactOptions,
    loading: loadingCompanyContactData,
    disabled: !selectedCompany || loadingCompanyContactData || !contactOptions?.length,
  };
  //#endregion
  //
  //#region Asignación de móvil por ticket
  const selectedTicketIds = Form.useWatch('tickets', formProps.form);
  const selectedTicketIdsNormalized = useMemo(
    () =>
      Array.isArray(selectedTicketIds)
        ? selectedTicketIds
        : selectedTicketIds !== undefined && selectedTicketIds !== null
          ? [selectedTicketIds]
          : [],
    [selectedTicketIds],
  );
  const selectedTicketOptions = useMemo(
    () =>
      (ticketsSelectPropsWithInitials.options ?? []).filter(option =>
        selectedTicketIdsNormalized.includes(option.value),
      ),
    [selectedTicketIdsNormalized, ticketsSelectPropsWithInitials.options],
  );
  const selectedTicketsResolved = useMemo(() => {
    if (!selectedTicketIdsNormalized.length) return true;

    return (
      selectedTicketOptions.length === selectedTicketIdsNormalized.length &&
      selectedTicketOptions.every(option => !!option.item)
    );
  }, [selectedTicketIdsNormalized, selectedTicketOptions]);
  const selectedTickets = useMemo(
    () => selectedTicketOptions.map(option => option.item).filter(Boolean) as Ticket[],
    [selectedTicketOptions],
  );
  // Obtención de móviles asociados a los tickets seleccionados
  const ticketMachineOptions: { value: number; label: string }[] = useMemo(() => {
    const unique = new Map<number, { value: number; label: string }>();

    selectedTickets.forEach(ticket => {
      if (!ticket.movil) return;
      if (unique.has(ticket.movil)) return;
      unique.set(ticket.movil, { value: ticket.movil, label: ticket.movil_str ?? '' });
    });

    return [...unique.values()];
  }, [selectedTickets]);

  // Seteo de los móviles asociados a los tickets en el formulario
  useEffect(() => {
    if (!selectedTicketIdsNormalized.length) {
      formProps.form?.setFieldValue('moviles', []);

      return;
    }

    if (!selectedTicketsResolved) return;

    formProps.form?.setFieldValue(
      'moviles',
      ticketMachineOptions.map(option => option.value),
    );
  }, [
    formProps.form,
    selectedTicketIdsNormalized.length,
    selectedTicketsResolved,
    ticketMachineOptions,
  ]);
  //#endregion

  //#region Asignación de precios fijos para instalación
  // Asignación de precios fijos para instalación
  const serviceType = Form.useWatch('type', formProps.form);
  // Almacenamiento de objeto del técnico para asignación de precios
  const modifiedTechnicianSelectProps = useMemo(() => {
    return {
      ...technicianSelectPropsWithInitials,
      onSelect: (value: any, option: any) => {
        const technicianItem = option.item as Technician;

        setSelectedTechnician(technicianItem);

        if (technicianSelectPropsWithInitials.onSelect) {
          technicianSelectPropsWithInitials.onSelect(value, option);
        }
      },
    };
  }, [technicianSelectPropsWithInitials]);
  const assignTechnicianPrice = useMemo(() => {
    if (!selectedTechnician) {
      return undefined;
    }

    const {
      external_hour_price,
      external_km_price,
      internal_hour_price,
      internal_km_price,
      moneda,
    } = selectedTechnician;
    let assignFunction: Function | undefined = undefined;

    if (!moneda) {
      return undefined;
    }

    if (serviceType == SERVICE_ORDER_TYPE.INSTALACION) {
      if (internal_hour_price || internal_km_price) {
        assignFunction = () => {
          formProps.form?.setFieldsValue({
            currency: moneda.id,
            hourly_rate: internal_hour_price,
            kilometres_rate: internal_km_price,
          });
        };
      }
    } else {
      if (external_hour_price || external_km_price) {
        assignFunction = () => {
          formProps.form?.setFieldsValue({
            currency: moneda.id,
            hourly_rate: external_hour_price,
            kilometres_rate: external_km_price,
          });
        };
      }
    }

    return assignFunction;
  }, [formProps.form, selectedTechnician, serviceType]);
  //#endregion
  const createdAutomatically = useMemo(
    () => formInitialValues?.created_automatically,
    [formInitialValues],
  );

  return {
    formInitialValues,
    technicianSelectProps: modifiedTechnicianSelectProps,
    companySelectProps: companySelectPropsWithInitials,
    ticketsSelectProps: ticketsSelectPropsWithInitials,
    currencySelectProps,
    ticketMachineOptions,
    ticketMachineOptionsLoading: !selectedTicketsResolved && !!selectedTicketIdsNormalized.length,
    companyContactsSelectProps,
    companyContactRules,
    assignTechnicianPrice,
    clearDraft,
    createdAutomatically,
  };
};

export default useServiceOrderForm;
